import { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, FileText, Calendar, Target, MessageSquare, BookOpen, 
  Upload, Download, Trash2, Plus, Check, Clock, TrendingUp,
  Send, User, Award, Link, Copy, DollarSign, GraduationCap,
  ShoppingBag, Briefcase, BarChart3, CreditCard, ExternalLink
} from 'lucide-react';

import { API } from '@/config/api';

// Learning modules for the affiliate program
const learningModules = [
  {
    id: 'getting-started',
    title: 'Getting Started as an Affiliate',
    lessons: [
      { id: 'l1', title: 'Welcome to NRL Affiliate Program', duration: '5 min', type: 'video' },
      { id: 'l2', title: 'Understanding Your Dashboard', duration: '8 min', type: 'article' },
      { id: 'l3', title: 'Setting Up Your Profile', duration: '5 min', type: 'article' },
      { id: 'l4', title: 'Commission Structure Explained', duration: '10 min', type: 'video' },
    ]
  },
  {
    id: 'marketing',
    title: 'Marketing & Promotion',
    lessons: [
      { id: 'l5', title: 'Social Media Best Practices', duration: '12 min', type: 'video' },
      { id: 'l6', title: 'Creating Compelling Content', duration: '15 min', type: 'article' },
      { id: 'l7', title: 'Email Marketing Fundamentals', duration: '10 min', type: 'video' },
      { id: 'l8', title: 'Building Your Personal Brand', duration: '12 min', type: 'article' },
    ]
  },
  {
    id: 'sales',
    title: 'Sales & Conversion',
    lessons: [
      { id: 'l9', title: 'Understanding Your Audience', duration: '10 min', type: 'article' },
      { id: 'l10', title: 'Effective Sales Conversations', duration: '15 min', type: 'video' },
      { id: 'l11', title: 'Handling Objections', duration: '12 min', type: 'article' },
      { id: 'l12', title: 'Closing Techniques', duration: '10 min', type: 'video' },
    ]
  },
];

// Quick action cards for navigation
const quickActions = [
  { id: 'trading', title: 'Trading Terminal', desc: 'Access live trading', icon: TrendingUp, link: '/trading', color: 'emerald' },
  { id: 'booking', title: 'Book Consultation', desc: 'Schedule a session', icon: Calendar, link: '/booking', color: 'blue' },
  { id: 'shop', title: 'Legacy Shop', desc: 'Browse products', icon: ShoppingBag, link: 'https://nrluniversity.com/shop', external: true, color: 'purple' },
  { id: 'study', title: 'The Gilded Study', desc: 'Educational courses', icon: GraduationCap, link: '/gilded-study', color: 'orange' },
  { id: 'portfolio', title: 'Portfolio Analytics', desc: 'Track performance', icon: BarChart3, link: '/portfolio-analytics', color: 'cyan' },
  { id: 'membership', title: 'Upgrade Membership', desc: 'Unlock premium features', icon: CreditCard, link: '#membership', color: 'gold' },
];

export default function AffiliatePortal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [documents, setDocuments] = useState([]);
  const [leads, setLeads] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [stats, setStats] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [completedLessons, setCompletedLessons] = useState([]);
  const [newLead, setNewLead] = useState({ name: '', email: '', notes: '' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [membershipModalOpen, setMembershipModalOpen] = useState(false);
  const chatEndRef = useRef(null);

  // Fetch all data on mount
  useEffect(() => {
    if (user) {
      fetchAllData();
      loadCompletedLessons();
    }
  }, [user]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [statsRes, leadsRes, docsRes, chatRes] = await Promise.all([
        fetch(`${API}/affiliate/stats`, { credentials: 'include' }).catch(() => null),
        fetch(`${API}/affiliate/leads`, { credentials: 'include' }).catch(() => null),
        fetch(`${API}/affiliate/documents`, { credentials: 'include' }).catch(() => null),
        fetch(`${API}/affiliate/chat`, { credentials: 'include' }).catch(() => null)
      ]);

      if (statsRes?.ok) setStats(await statsRes.json());
      if (leadsRes?.ok) {
        const data = await leadsRes.json();
        setLeads(Array.isArray(data) ? data : []);
      }
      if (docsRes?.ok) {
        const data = await docsRes.json();
        setDocuments(Array.isArray(data) ? data : []);
      }
      if (chatRes?.ok) {
        const data = await chatRes.json();
        setChatMessages(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      console.error('Fetch error:', e);
    }
    setLoading(false);
  };

  const loadCompletedLessons = () => {
    const saved = localStorage.getItem(`nrl_completed_lessons_${user?.user_id}`);
    if (saved) {
      setCompletedLessons(JSON.parse(saved));
    }
  };

  const saveCompletedLessons = (lessons) => {
    localStorage.setItem(`nrl_completed_lessons_${user?.user_id}`, JSON.stringify(lessons));
  };

  // Generate referral link
  const referralCode = user?.user_id?.slice(0, 8) || 'affiliate';
  const referralLink = `https://nouveaurichelegacy.com/ref/${referralCode}`;

  // Calculate stats
  const totalLessons = learningModules.reduce((acc, m) => acc + m.lessons.length, 0);
  const progressPercent = Math.round((completedLessons.length / totalLessons) * 100);

  const displayStats = {
    earnings: stats?.total_earnings || 0,
    totalLeads: stats?.total_leads || leads.length,
    convertedLeads: stats?.converted_leads || 0,
    totalReferrals: stats?.total_referrals || 0,
    tier: stats?.tier || 'Legacy Explorer',
    membershipStatus: user?.membership_tier || 'legacy_explorer',
  };

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await fetch(`${API}/affiliate/documents/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        setDocuments([data.document, ...documents]);
        toast.success('Document uploaded successfully!');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (e) {
      toast.error('Upload failed. Please try again.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;

    try {
      const res = await fetch(`${API}/affiliate/documents/${docId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (res.ok) {
        setDocuments(documents.filter(d => d.document_id !== docId));
        toast.success('Document deleted');
      } else {
        toast.error('Failed to delete document');
      }
    } catch (e) {
      toast.error('Failed to delete document');
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    const msg = {
      message_id: `msg_${Date.now()}`,
      user_name: user?.name || 'You',
      message: newMessage,
      timestamp: new Date().toISOString(),
      isOwn: true
    };

    setChatMessages([...chatMessages, msg]);
    setNewMessage('');

    try {
      await fetch(`${API}/affiliate/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ message: newMessage })
      });
    } catch (e) {
      console.error('Chat send error:', e);
    }
  };

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error('Please fill in name and email');
      return;
    }

    const lead = {
      lead_id: `lead_${Date.now()}`,
      ...newLead,
      status: 'New',
      created_at: new Date().toISOString()
    };

    try {
      const res = await fetch(`${API}/affiliate/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newLead)
      });

      if (res.ok) {
        const data = await res.json();
        setLeads([data.lead || lead, ...leads]);
      } else {
        setLeads([lead, ...leads]);
      }
    } catch (e) {
      setLeads([lead, ...leads]);
    }

    setNewLead({ name: '', email: '', notes: '' });
    toast.success('Lead added successfully!');
  };

  const toggleLessonComplete = (lessonId) => {
    let updated;
    if (completedLessons.includes(lessonId)) {
      updated = completedLessons.filter(id => id !== lessonId);
    } else {
      updated = [...completedLessons, lessonId];
      toast.success('Lesson completed! 🎉');
    }
    setCompletedLessons(updated);
    saveCompletedLessons(updated);
  };

  const copyReferralLink = () => {
    navigator.clipboard.writeText(referralLink);
    toast.success('Referral link copied!');
  };

  const handleQuickAction = (action) => {
    if (action.external) {
      window.open(action.link, '_blank');
    } else if (action.link === '#membership') {
      setMembershipModalOpen(true);
    } else {
      navigate(action.link);
    }
  };

  const getColorClass = (color) => {
    const colors = {
      emerald: 'bg-emerald-500/20 text-emerald-400',
      blue: 'bg-blue-500/20 text-blue-400',
      purple: 'bg-purple-500/20 text-purple-400',
      orange: 'bg-orange-500/20 text-orange-400',
      cyan: 'bg-cyan-500/20 text-cyan-400',
      gold: 'bg-[#D4AF37]/20 text-[#D4AF37]',
    };
    return colors[color] || colors.gold;
  };

  return (
    <div className="min-h-screen bg-[#1c1814] pt-20 pb-12" data-testid="affiliate-portal">
      {/* Header */}
      <div className="border-b border-[#D4AF37]/20 bg-[#1c1814]">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold text-white">
                Welcome, <span className="text-[#D4AF37]">{user?.name?.split(' ')[0] || 'Partner'}</span>
              </h1>
              <p className="text-gray-400 mt-1">
                Your Affiliate Portal • {displayStats.tier}
              </p>
            </div>
            
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="bg-[#252540] border border-[#D4AF37]/20 flex-wrap">
                <TabsTrigger value="dashboard" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <TrendingUp className="h-4 w-4 mr-2" /> Dashboard
                </TabsTrigger>
                <TabsTrigger value="documents" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <FileText className="h-4 w-4 mr-2" /> Documents
                </TabsTrigger>
                <TabsTrigger value="leads" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <Target className="h-4 w-4 mr-2" /> Leads
                </TabsTrigger>
                <TabsTrigger value="learning" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <BookOpen className="h-4 w-4 mr-2" /> Learning
                </TabsTrigger>
                <TabsTrigger value="chat" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
                  <MessageSquare className="h-4 w-4 mr-2" /> Chat
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6">
            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {quickActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleQuickAction(action)}
                  className="bg-[#252540] border border-[#D4AF37]/20 rounded-lg p-4 hover:border-[#D4AF37]/50 transition-colors text-left group"
                  data-testid={`quick-action-${action.id}`}
                >
                  <div className={`p-2 rounded-lg w-fit mb-3 ${getColorClass(action.color)}`}>
                    <action.icon className="h-5 w-5" />
                  </div>
                  <p className="text-white font-medium text-sm group-hover:text-[#D4AF37] transition-colors">
                    {action.title}
                  </p>
                  <p className="text-gray-500 text-xs mt-1">{action.desc}</p>
                  {action.external && <ExternalLink className="h-3 w-3 text-gray-500 mt-2" />}
                </button>
              ))}
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="bg-[#252540] border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-emerald-500/20 rounded-lg">
                      <DollarSign className="h-6 w-6 text-emerald-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Earnings</p>
                      <p className="text-2xl font-bold text-[#D4AF37]">${displayStats.earnings.toFixed(2)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#252540] border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-blue-500/20 rounded-lg">
                      <Target className="h-6 w-6 text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Leads</p>
                      <p className="text-2xl font-bold text-white">{displayStats.totalLeads}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#252540] border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-purple-500/20 rounded-lg">
                      <Users className="h-6 w-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Referrals</p>
                      <p className="text-2xl font-bold text-white">{displayStats.totalReferrals}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-[#252540] border-[#D4AF37]/20">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-orange-500/20 rounded-lg">
                      <BookOpen className="h-6 w-6 text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase">Training</p>
                      <p className="text-2xl font-bold text-white">{progressPercent}%</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Referral Link */}
            <Card className="bg-[#252540] border-[#D4AF37]/20">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="text-white flex items-center gap-2">
                  <Link className="h-5 w-5 text-[#D4AF37]" /> Your Referral Link
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex gap-2">
                  <Input 
                    value={referralLink}
                    readOnly
                    className="bg-[#1c1814] border-[#D4AF37]/30 text-white"
                  />
                  <Button onClick={copyReferralLink} className="bg-[#D4AF37] text-black hover:bg-[#B8962E]">
                    <Copy className="h-4 w-4 mr-2" /> Copy
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Training Progress */}
            <Card className="bg-[#252540] border-[#D4AF37]/20">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="text-white">Training Progress</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="flex items-center gap-4 mb-2">
                  <Progress value={progressPercent} className="flex-1 h-3" />
                  <span className="text-[#D4AF37] font-bold">{progressPercent}%</span>
                </div>
                <p className="text-sm text-gray-400">
                  {completedLessons.length} of {totalLessons} lessons completed
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="space-y-6">
            <Card className="bg-[#252540] border-[#D4AF37]/20">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-white">My Documents</CardTitle>
                  <div>
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      className="hidden"
                      accept=".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png"
                    />
                    <Button 
                      onClick={() => fileInputRef.current?.click()} 
                      disabled={uploading}
                      className="bg-[#D4AF37] text-black hover:bg-[#B8962E]"
                      data-testid="upload-document-btn"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload Document'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {documents.length === 0 ? (
                  <div className="p-12 text-center">
                    <FileText className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No documents yet</p>
                    <p className="text-sm text-gray-500 mt-1">Upload your first document to get started</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#D4AF37]/10">
                    {documents.map((doc) => (
                      <div key={doc.document_id} className="p-4 flex items-center justify-between hover:bg-[#D4AF37]/5">
                        <div className="flex items-center gap-4">
                          <div className="p-3 bg-[#D4AF37]/10 rounded">
                            <FileText className="h-6 w-6 text-[#D4AF37]" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{doc.name || doc.filename}</p>
                            <p className="text-xs text-gray-500">
                              {doc.type || 'Document'} • {doc.size || 'Unknown size'} • {new Date(doc.created_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {doc.url && (
                            <Button variant="ghost" size="icon" className="text-[#D4AF37]" asChild>
                              <a href={doc.url} target="_blank" rel="noopener noreferrer">
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            className="text-red-400"
                            onClick={() => handleDeleteDocument(doc.document_id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Leads Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-6">
            <Card className="bg-[#252540] border-[#D4AF37]/20">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="text-white">Add New Lead</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="grid md:grid-cols-4 gap-4">
                  <Input
                    placeholder="Name"
                    value={newLead.name}
                    onChange={(e) => setNewLead({...newLead, name: e.target.value})}
                    className="bg-[#1c1814] border-[#D4AF37]/30 text-white"
                  />
                  <Input
                    placeholder="Email"
                    type="email"
                    value={newLead.email}
                    onChange={(e) => setNewLead({...newLead, email: e.target.value})}
                    className="bg-[#1c1814] border-[#D4AF37]/30 text-white"
                  />
                  <Input
                    placeholder="Notes (optional)"
                    value={newLead.notes}
                    onChange={(e) => setNewLead({...newLead, notes: e.target.value})}
                    className="bg-[#1c1814] border-[#D4AF37]/30 text-white"
                  />
                  <Button onClick={handleAddLead} className="bg-[#D4AF37] text-black hover:bg-[#B8962E]">
                    <Plus className="h-4 w-4 mr-2" /> Add Lead
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-[#252540] border-[#D4AF37]/20">
              <CardHeader className="border-b border-[#D4AF37]/20">
                <CardTitle className="text-white">My Leads ({leads.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {leads.length === 0 ? (
                  <div className="p-12 text-center">
                    <Target className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No leads yet</p>
                    <p className="text-sm text-gray-500 mt-1">Add your first lead above</p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#D4AF37]/10">
                    {leads.map((lead) => (
                      <div key={lead.lead_id} className="p-4 hover:bg-[#D4AF37]/5">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-[#D4AF37]/10 rounded-full">
                              <User className="h-5 w-5 text-[#D4AF37]" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{lead.name}</p>
                              <p className="text-xs text-gray-500">{lead.email}</p>
                            </div>
                          </div>
                          <Badge className={
                            lead.status === 'New' ? 'bg-blue-500/20 text-blue-400' :
                            lead.status === 'Contacted' ? 'bg-yellow-500/20 text-yellow-400' :
                            lead.status === 'Converted' ? 'bg-emerald-500/20 text-emerald-400' :
                            'bg-gray-500/20 text-gray-400'
                          }>
                            {lead.status}
                          </Badge>
                        </div>
                        {lead.notes && <p className="text-sm text-gray-400 mt-2 ml-12">{lead.notes}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Learning Tab */}
        {activeTab === 'learning' && (
          <div className="space-y-6">
            <Card className="bg-[#252540] border-[#D4AF37]/20">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white">Your Learning Progress</h3>
                    <p className="text-sm text-gray-400">{completedLessons.length} of {totalLessons} lessons completed</p>
                  </div>
                  <div className="text-right">
                    <p className="text-4xl font-bold text-[#D4AF37]">{progressPercent}%</p>
                  </div>
                </div>
                <Progress value={progressPercent} className="h-4" />
              </CardContent>
            </Card>

            <Accordion type="single" collapsible className="space-y-4">
              {learningModules.map((module) => {
                const moduleCompleted = module.lessons.filter(l => completedLessons.includes(l.id)).length;
                const isModuleComplete = moduleCompleted === module.lessons.length;
                
                return (
                  <AccordionItem 
                    key={module.id} 
                    value={module.id}
                    className="bg-[#252540] border border-[#D4AF37]/20 rounded-lg overflow-hidden"
                  >
                    <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-[#D4AF37]/5">
                      <div className="flex items-center gap-4 w-full">
                        <div className={`p-3 rounded-lg ${isModuleComplete ? 'bg-emerald-500/20' : 'bg-[#D4AF37]/20'}`}>
                          {isModuleComplete ? 
                            <Award className="h-6 w-6 text-emerald-400" /> :
                            <BookOpen className="h-6 w-6 text-[#D4AF37]" />
                          }
                        </div>
                        <div className="flex-1 text-left">
                          <p className="text-white font-medium">{module.title}</p>
                          <p className="text-sm text-gray-500">{moduleCompleted}/{module.lessons.length} lessons</p>
                        </div>
                        <Progress value={(moduleCompleted / module.lessons.length) * 100} className="w-24 h-2" />
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-6 pb-4">
                      <div className="space-y-2 mt-2">
                        {module.lessons.map((lesson) => {
                          const isCompleted = completedLessons.includes(lesson.id);
                          return (
                            <button
                              key={lesson.id}
                              onClick={() => toggleLessonComplete(lesson.id)}
                              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                                isCompleted 
                                  ? 'bg-emerald-500/10 border border-emerald-500/30' 
                                  : 'bg-[#1c1814] hover:bg-[#D4AF37]/10 border border-transparent'
                              }`}
                            >
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isCompleted ? 'bg-emerald-500' : 'border-2 border-[#D4AF37]/30'
                              }`}>
                                {isCompleted && <Check className="h-4 w-4 text-white" />}
                              </div>
                              <div className="flex-1 text-left">
                                <p className={isCompleted ? 'text-emerald-400' : 'text-white'}>
                                  {lesson.title}
                                </p>
                              </div>
                              <Badge variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37]">
                                {lesson.type}
                              </Badge>
                              <span className="text-xs text-gray-500">{lesson.duration}</span>
                            </button>
                          );
                        })}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </div>
        )}

        {/* Chat Tab */}
        {activeTab === 'chat' && (
          <Card className="bg-[#252540] border-[#D4AF37]/20 h-[calc(100vh-250px)] flex flex-col">
            <CardHeader className="border-b border-[#D4AF37]/20">
              <CardTitle className="text-white flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#D4AF37]" /> Affiliate Community Chat
              </CardTitle>
              <CardDescription className="text-gray-400">
                Connect with other affiliates and get support
              </CardDescription>
            </CardHeader>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {chatMessages.length === 0 ? (
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-500 mx-auto mb-4" />
                    <p className="text-gray-400">No messages yet</p>
                    <p className="text-sm text-gray-500">Start the conversation!</p>
                  </div>
                ) : (
                  chatMessages.map((msg) => (
                    <div key={msg.message_id} className={`flex gap-3 ${msg.isOwn ? 'flex-row-reverse' : ''}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                        msg.isAdmin ? 'bg-[#D4AF37]' : 'bg-[#1c1814]'
                      }`}>
                        <User className={`h-5 w-5 ${msg.isAdmin ? 'text-black' : 'text-white'}`} />
                      </div>
                      <div className={`max-w-[70%] ${msg.isOwn ? 'text-right' : ''}`}>
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-medium ${msg.isAdmin ? 'text-[#D4AF37]' : 'text-white'}`}>
                            {msg.user_name}
                          </span>
                          {msg.isAdmin && <Badge className="bg-[#D4AF37]/20 text-[#D4AF37]">Admin</Badge>}
                          <span className="text-xs text-gray-500">
                            {new Date(msg.timestamp).toLocaleTimeString()}
                          </span>
                        </div>
                        <div className={`p-3 rounded-lg ${
                          msg.isOwn ? 'bg-[#D4AF37]/20 border border-[#D4AF37]/30' : 'bg-[#1c1814]'
                        }`}>
                          <p className="text-white text-sm">{msg.message}</p>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>
            </ScrollArea>
            <div className="border-t border-[#D4AF37]/20 p-4">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder="Type your message..."
                  className="bg-[#1c1814] border-[#D4AF37]/30 text-white"
                />
                <Button onClick={handleSendMessage} className="bg-[#D4AF37] text-black hover:bg-[#B8962E]">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </Card>
        )}
      </div>

      {/* Membership Upgrade Modal */}
      <Dialog open={membershipModalOpen} onOpenChange={setMembershipModalOpen}>
        <DialogContent className="bg-[#252540] border-[#D4AF37]/20 max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white">Upgrade Your Membership</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-3">
              <button
                onClick={() => { setMembershipModalOpen(false); navigate('/booking'); }}
                className="p-4 bg-[#1c1814] border border-[#D4AF37]/20 rounded-lg hover:border-[#D4AF37]/50 text-left"
              >
                <p className="text-white font-medium">Wealth Architect</p>
                <p className="text-[#D4AF37] font-bold">$497/month</p>
                <p className="text-sm text-gray-400 mt-1">Trading Terminal, Portfolio Analytics, Trade Journal</p>
              </button>
              <button
                onClick={() => { setMembershipModalOpen(false); navigate('/booking'); }}
                className="p-4 bg-[#D4AF37]/10 border border-[#D4AF37] rounded-lg text-left"
              >
                <Badge className="bg-[#D4AF37] text-black mb-2">Popular</Badge>
                <p className="text-white font-medium">Dynasty Builder</p>
                <p className="text-[#D4AF37] font-bold">$1,497/month</p>
                <p className="text-sm text-gray-400 mt-1">Everything + Copy Trading, Staking, AI Advisor, VIP Support</p>
              </button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMembershipModalOpen(false)} className="border-gray-600 text-gray-400">
              Maybe Later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
